import { useMemo, useState } from 'react';
import { SITE_DOMAIN, seoPages, sitelinkPages, type SeoPage } from '../../content/seo';

const TITLE_SOFT = 60;
const DESC_SOFT = 160;

function lenTone(n: number, soft: number) {
  if (n === 0) return '#b42318';
  if (n > soft) return '#b54708';
  if (n >= soft - 10) return '#027a48';
  return '#667085';
}

function MicusMark({ size = 18 }: { size?: number }) {
  return (
    <img
      src="/search-engine-logo.png"
      alt=""
      width={size}
      height={size}
      style={{ width: size, height: size, objectFit: 'contain', borderRadius: 4, background: '#fff', flexShrink: 0 }}
    />
  );
}

function Field({ label, value, limit }: { label: string; value: string; limit?: number }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#667085' }}>{label}</span>
        {limit != null && (
          <span style={{ fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: lenTone(value.length, limit) }}>
            {value.length}/{limit}
          </span>
        )}
      </div>
      <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: '#101828', whiteSpace: 'pre-wrap' }}>{value}</p>
    </div>
  );
}

/** Hidden developer lab — not linked from the public site. */
export default function DevSeoPage() {
  const pages = useMemo(() => seoPages.filter((p) => !p.path.startsWith('/__')), [ ]);
  const [path, setPath] = useState('/');
  const page = pages.find((p) => p.path === path) ?? pages[0];
  const featured = [pages[0], pages.find((p) => p.path === '/credit-rating')!, pages.find((p) => p.path === '/fundraising')!, pages.find((p) => p.path === '/pricing')!].filter(Boolean);

  return (
    <div style={{ minHeight: '100vh', background: '#f2f4f7', color: '#101828', fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>
      <header style={{ background: '#101828', color: '#f2f4f7', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <MicusMark size={22} />
        <strong style={{ fontSize: 13, letterSpacing: '0.04em' }}>DEV · SEO / AI preview</strong>
        <span style={{ fontSize: 12, color: '#98a2b3' }}>/__dev/seo · noindex · not in nav</span>
        <code style={{ marginLeft: 'auto', fontSize: 12, background: '#1d2939', padding: '4px 8px', borderRadius: 6 }}>
          {SITE_DOMAIN}{page.path}
        </code>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', minHeight: 'calc(100vh - 48px)' }} className="dev-seo-grid">
        <aside style={{ background: '#fff', borderRight: '1px solid #e4e7ec', padding: 12 }}>
          <p style={{ margin: '8px 8px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#667085' }}>
            Pages
          </p>
          {pages.map((p) => {
            const on = p.path === page.path;
            return (
              <button
                key={p.path}
                type="button"
                onClick={() => setPath(p.path)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: 0,
                  borderRadius: 8,
                  padding: '8px 10px',
                  marginBottom: 2,
                  cursor: 'pointer',
                  background: on ? '#f4ebff' : 'transparent',
                  color: on ? '#6941c6' : '#344054',
                  fontWeight: on ? 700 : 500,
                  fontSize: 13,
                }}
              >
                <span style={{ display: 'block' }}>{p.sitelinkLabel || p.title.split('|')[0].trim()}</span>
                <span style={{ display: 'block', fontSize: 11, fontFamily: 'ui-monospace, Menlo, monospace', color: '#98a2b3', fontWeight: 500 }}>{p.path}</span>
              </button>
            );
          })}
        </aside>

        <main style={{ padding: 20, overflow: 'auto' }}>
          <section style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 10, padding: 18, marginBottom: 16 }}>
            <h1 style={{ margin: '0 0 14px', fontSize: 16 }}>Meta for {page.path}</h1>
            <Field label="Title" value={page.title} limit={TITLE_SOFT} />
            <Field label="Description" value={page.description} limit={DESC_SOFT} />
            <Field label="Keywords" value={page.keywords} />
            <Field label="AI blurb" value={page.aiBlurb} />
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.85fr', gap: 16 }} className="dev-seo-previews">
            <section style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #e4e7ec', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#667085' }}>
                Search engine
              </div>
              <div style={{ padding: 18, fontFamily: 'Arial, Helvetica, sans-serif' }}>
                <GoogleResult page={page} sitelinks={page.path === '/'} />
              </div>
            </section>

            <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <AiCard name="ChatGPT" accent="#10a37f" page={page} />
              <PerplexityCard pages={featured} />
              <AiCard name="Gemini" accent="#1a73e8" page={page} />
            </section>
          </div>
        </main>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .dev-seo-grid { grid-template-columns: 1fr !important; }
          .dev-seo-previews { grid-template-columns: 1fr !important; }
        }
        .dev-serp-title { color: #1a0dab; font-size: 20px; line-height: 1.3; text-decoration: none; display: inline-block; margin: 2px 0 4px; }
        .dev-serp-title:hover { text-decoration: underline; }
      `}</style>
    </div>
  );
}

function GoogleResult({ page, sitelinks }: { page: SeoPage; sitelinks?: boolean }) {
  const path = page.path === '/' ? '' : page.path.replace(/^\//, '');
  const crumb = path ? `${SITE_DOMAIN} › ${path.replace(/\//g, ' › ')}` : SITE_DOMAIN;
  return (
    <article>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <MicusMark size={20} />
        <div>
          <p style={{ margin: 0, fontSize: 14 }}>Micus</p>
          <p style={{ margin: 0, fontSize: 12, color: '#4d5156' }}>{crumb}</p>
        </div>
      </div>
      <a href={page.path} className="dev-serp-title">{page.title}</a>
      <p style={{ margin: '0 0 12px', fontSize: 14, lineHeight: 1.58, color: '#4d5156' }}>{page.description}</p>
      {sitelinks && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px' }}>
          {sitelinkPages.slice(0, 6).map((p) => (
            <a key={p.path} href={p.path} style={{ textDecoration: 'none' }}>
              <span style={{ display: 'block', fontSize: 14, color: '#1a0dab' }}>{p.sitelinkLabel}</span>
              <span style={{ display: 'block', fontSize: 13, color: '#4d5156' }}>{p.sitelinkHint}</span>
            </a>
          ))}
        </div>
      )}
    </article>
  );
}

function AiCard({ name, accent, page }: { name: string; accent: string; page: SeoPage }) {
  return (
    <article style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: accent }} />
        <strong style={{ fontSize: 12 }}>{name}</strong>
        <span style={{ fontSize: 11, color: '#98a2b3' }}>citation</span>
      </div>
      <p style={{ margin: '0 0 10px', fontSize: 13, lineHeight: 1.55, color: '#344054' }}>{page.aiBlurb}</p>
      <a href={page.path} style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'inherit', background: '#f9fafb', border: '1px solid #e4e7ec', borderRadius: 8, padding: '8px 10px' }}>
        <MicusMark size={18} />
        <span>
          <b style={{ display: 'block', fontSize: 12.5 }}>{page.title}</b>
          <em style={{ display: 'block', fontStyle: 'normal', fontSize: 11, color: '#667085' }}>{SITE_DOMAIN}{page.path === '/' ? '' : page.path}</em>
        </span>
      </a>
    </article>
  );
}

function PerplexityCard({ pages }: { pages: SeoPage[] }) {
  return (
    <article style={{ background: '#0d1117', color: '#e6edf3', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: '#20808d' }} />
        <strong style={{ fontSize: 12 }}>Perplexity</strong>
        <span style={{ fontSize: 11, color: '#8b949e' }}>sources</span>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
        {pages.map((p, i) => (
          <li key={p.path}>
            <a href={p.path} style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'inherit', background: '#161b22', borderRadius: 8, padding: '7px 8px' }}>
              <span style={{ width: 18, height: 18, borderRadius: 99, background: '#20808d', color: '#fff', fontSize: 10, fontWeight: 800, display: 'grid', placeItems: 'center' }}>{i + 1}</span>
              <MicusMark size={16} />
              <span>
                <b style={{ display: 'block', fontSize: 12.5 }}>{p.sitelinkLabel || 'Micus'}</b>
                <em style={{ display: 'block', fontStyle: 'normal', fontSize: 11, color: '#8b949e' }}>{SITE_DOMAIN}{p.path === '/' ? '' : p.path}</em>
              </span>
            </a>
          </li>
        ))}
      </ol>
    </article>
  );
}
